/**
 * copyFormats.ts - Serializers for TransactionTable's copy menu
 *
 * Three output formats for pasting into LLM chats, spreadsheets, or clipboard:
 * - JSON: Structured for programmatic use
 * - Markdown: Table format for Claude/ChatGPT
 * - Plain text: Compact for inline chat
 *
 * Ported from TekStak's TransactionsDrilldownModal (MUI-free, zero deps).
 */
function fmtCurrency(n) {
    const v = Number(n) || 0;
    if (!Number.isFinite(v))
        return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(v);
}
function fmtDate(d) {
    if (!d)
        return null;
    try {
        return new Date(d).toISOString().slice(0, 10);
    }
    catch {
        return null;
    }
}
/**
 * Project a raw row into a stable, LLM-friendly shape.
 * Strip joins/internal ids; keep the fields a human would want.
 */
function projectRow(r) {
    return {
        po_number: r.purchase_document_num || null,
        line: r.purchase_document_line || null,
        agency: r.agency || null,
        supplier: r.supplier_name || null,
        component: r.component_name || null,
        description: r.item_description || null,
        confidence: r.component_confidence != null
            ? Number((r.component_confidence * 100).toFixed(0))
            : null,
        classification_method: r.classification_method || null,
        amount: r.line_total != null ? Number(r.line_total) : null,
        start_date: fmtDate(r.start_date),
        end_date: fmtDate(r.end_date),
    };
}
export function toJSON(rows, context) {
    const projected = rows.map(projectRow);
    const total = projected.reduce((sum, r) => sum + (r.amount || 0), 0);
    const payload = {
        ...(context || {}),
        row_count: projected.length,
        total_amount: total,
        rows: projected,
    };
    return JSON.stringify(payload, null, 2);
}
export function toMarkdown(rows, context) {
    const projected = rows.map(projectRow);
    const total = projected.reduce((sum, r) => sum + (r.amount || 0), 0);
    const lines = [];
    if (context?.title)
        lines.push(`# ${context.title}`);
    const meta = [];
    if (context?.vendor)
        meta.push(`**Vendor:** ${context.vendor}`);
    if (context?.department)
        meta.push(`**Agency:** ${context.department}`);
    if (context?.period)
        meta.push(`**Period:** ${context.period}`);
    if (context?.filters)
        meta.push(`**Filters:** ${context.filters}`);
    if (meta.length)
        lines.push(meta.join(' · '));
    lines.push(`**${projected.length} rows · total ${fmtCurrency(total)}**`);
    lines.push('');
    lines.push('| PO # | Line | Agency | Supplier | Component | Description | Confidence | Method | Amount |');
    lines.push('|---|---|---|---|---|---|---|---|---|');
    for (const r of projected) {
        const desc = (r.description || '').replace(/\|/g, '\\|').slice(0, 120);
        lines.push([
            r.po_number || '—',
            r.line ?? '—',
            r.agency || '—',
            r.supplier || '—',
            r.component || '—',
            desc || '—',
            r.confidence != null ? `${r.confidence}%` : '—',
            r.classification_method || '—',
            r.amount != null ? fmtCurrency(r.amount) : '—',
        ].join(' | ').replace(/^/, '| ').replace(/$/, ' |'));
    }
    return lines.join('\n');
}
export function toPlainText(rows, context) {
    const projected = rows.map(projectRow);
    const total = projected.reduce((sum, r) => sum + (r.amount || 0), 0);
    const lines = [];
    if (context?.title)
        lines.push(context.title);
    const meta = [];
    if (context?.vendor)
        meta.push(`Vendor: ${context.vendor}`);
    if (context?.department)
        meta.push(`Agency: ${context.department}`);
    if (context?.period)
        meta.push(`Period: ${context.period}`);
    if (context?.filters)
        meta.push(`Filters: ${context.filters}`);
    if (meta.length)
        lines.push(meta.join(' | '));
    lines.push(`${projected.length} rows | total ${fmtCurrency(total)}`);
    lines.push('');
    // Group by PO for readability in plain text.
    const byPo = new Map();
    for (const r of projected) {
        const key = r.po_number || '(no PO)';
        if (!byPo.has(key))
            byPo.set(key, []);
        byPo.get(key).push(r);
    }
    for (const [po, items] of byPo) {
        const poTotal = items.reduce((s, x) => s + (x.amount || 0), 0);
        lines.push(`PO ${po} — ${items[0].agency || '—'} — ${items[0].supplier || '—'} — ${fmtCurrency(poTotal)}`);
        for (const it of items) {
            const desc = (it.description || '').slice(0, 100);
            lines.push(`  Line ${it.line ?? '?'}: ${it.component || '—'} · ${fmtCurrency(it.amount || 0)}` +
                (desc ? ` · ${desc}` : '') +
                (it.confidence != null ? ` (${it.confidence}% ${it.classification_method || ''})` : ''));
        }
        lines.push('');
    }
    return lines.join('\n').trimEnd();
}
/**
 * Convert PO detail to JSON format.
 * Full structured data for programmatic use.
 */
export function poToJSON(po) {
    const payload = {
        document_number: po.header.document_number,
        agency: po.header.agency || po.header.official_name || null,
        supplier: po.header.supplier_name || null,
        supplier_number: po.header.supplier_number || null,
        buyer: po.header.buyer || null,
        buyer_email: po.header.buyer_email || null,
        po_date: fmtDate(po.header.purchase_order_date),
        status: po.header.po_status || null,
        acquisition_type: po.header.acquisition_type || null,
        acquisition_method: po.header.acquisition_method || null,
        contract_id: po.header.lpa_contract_id || null,
        term_start: fmtDate(po.totals.earliest_start),
        term_end: fmtDate(po.totals.latest_end),
        line_count: po.totals.line_count,
        total_amount: po.totals.total_spend,
        lines: po.lines.map(l => ({
            line: l.line || null,
            description: l.description || null,
            component: l.component_name || null,
            vendor: l.classified_vendor || null,
            confidence: l.component_confidence != null ? Math.round(l.component_confidence * 100) : null,
            unspsc_code: l.unspsc_code || null,
            quantity: l.quantity || null,
            uom: l.uom || null,
            unit_price: l.unit_price || null,
            amount: l.amount || 0,
        })),
    };
    return JSON.stringify(payload, null, 2);
}
/**
 * Convert PO detail to Markdown format.
 * Formatted as a proper PO document for pasting into emails or Claude.
 */
export function poToMarkdown(po) {
    const out = [];
    const h = po.header;
    const t = po.totals;
    // Header
    out.push(`# Purchase Order ${h.document_number}`);
    out.push('');
    // Parties
    out.push('## Parties');
    out.push(`- **Bill To:** ${h.agency || h.official_name || '—'}`);
    if (h.department_name && h.department_name !== h.agency) {
        out.push(`  - ${h.department_name}`);
    }
    out.push(`- **Vendor:** ${h.supplier_name || '—'}`);
    if (h.supplier_number)
        out.push(`  - Vendor #: ${h.supplier_number}`);
    out.push(`- **Buyer:** ${h.buyer || '—'}`);
    if (h.buyer_email)
        out.push(`  - Email: ${h.buyer_email}`);
    out.push('');
    // Details
    out.push('## Details');
    if (h.purchase_order_date)
        out.push(`- **PO Date:** ${fmtDate(h.purchase_order_date)}`);
    if (t.earliest_start || t.latest_end) {
        out.push(`- **Term:** ${fmtDate(t.earliest_start) || '?'} – ${fmtDate(t.latest_end) || '?'}`);
    }
    if (h.po_status)
        out.push(`- **Status:** ${h.po_status}`);
    if (h.acquisition_type)
        out.push(`- **Acquisition:** ${h.acquisition_type}`);
    if (h.acquisition_method)
        out.push(`  - Method: ${h.acquisition_method}`);
    if (h.lpa_contract_id)
        out.push(`- **Contract ID:** ${h.lpa_contract_id}`);
    out.push('');
    // Line Items Table
    out.push(`## Line Items (${t.line_count} items)`);
    out.push('');
    out.push('| Line | Description | Classification | Qty | Unit Price | Amount |');
    out.push('|---:|---|---|---:|---:|---:|');
    for (const l of po.lines) {
        const desc = (l.description || '—').replace(/\|/g, '\\|').slice(0, 80);
        const classification = l.component_name
            ? `${l.component_name}${l.classified_vendor ? ` (${l.classified_vendor})` : ''}`
            : '—';
        const qty = l.quantity != null ? String(l.quantity) + (l.uom ? ` ${l.uom}` : '') : '—';
        const unitPrice = l.unit_price != null ? fmtCurrency(l.unit_price) : '—';
        out.push(`| ${l.line ?? '—'} | ${desc} | ${classification} | ${qty} | ${unitPrice} | ${fmtCurrency(l.amount)} |`);
    }
    out.push('');
    // Total
    out.push(`**TOTAL: ${fmtCurrency(t.total_spend)}**`);
    // Vendor warnings
    if (t.distinct_classified_vendors && t.distinct_classified_vendors.length > 1) {
        out.push('');
        out.push(`*Mixed vendors: ${t.distinct_classified_vendors.join(', ')}*`);
    }
    return out.join('\n');
}
/**
 * Convert PO detail to plain text format.
 * Compact format for inline chat or quick paste.
 */
export function poToPlainText(po) {
    const out = [];
    const h = po.header;
    const t = po.totals;
    // Header line
    out.push(`PURCHASE ORDER ${h.document_number}`);
    out.push('='.repeat(40));
    // Parties block
    out.push(`Bill To: ${h.agency || h.official_name || '—'}`);
    out.push(`Vendor: ${h.supplier_name || '—'}${h.supplier_number ? ` (#${h.supplier_number})` : ''}`);
    out.push(`Buyer: ${h.buyer || '—'}${h.buyer_email ? ` <${h.buyer_email}>` : ''}`);
    out.push('');
    // Details
    const details = [];
    if (h.purchase_order_date)
        details.push(`Date: ${fmtDate(h.purchase_order_date)}`);
    if (h.po_status)
        details.push(`Status: ${h.po_status}`);
    if (h.acquisition_type)
        details.push(`Acquisition: ${h.acquisition_type}`);
    if (h.lpa_contract_id)
        details.push(`Contract: ${h.lpa_contract_id}`);
    if (details.length) {
        out.push(details.join(' | '));
        out.push('');
    }
    // Line items
    out.push(`LINE ITEMS (${t.line_count})`);
    out.push('-'.repeat(40));
    for (const l of po.lines) {
        const desc = (l.description || '—').slice(0, 60);
        const amtStr = fmtCurrency(l.amount);
        out.push(`${l.line ?? '?'}. ${desc}`);
        const classification = l.component_name
            ? `   → ${l.component_name}${l.classified_vendor ? ` (${l.classified_vendor})` : ''}`
            : '';
        const qtyLine = l.quantity != null
            ? `   Qty: ${l.quantity}${l.uom ? ` ${l.uom}` : ''}${l.unit_price != null ? ` @ ${fmtCurrency(l.unit_price)}` : ''}`
            : '';
        if (classification)
            out.push(classification);
        if (qtyLine)
            out.push(qtyLine);
        out.push(`   Amount: ${amtStr}`);
        out.push('');
    }
    // Total
    out.push('='.repeat(40));
    out.push(`TOTAL: ${fmtCurrency(t.total_spend)}`);
    return out.join('\n');
}
